import React, { useState, useRef, useCallback, Fragment } from 'react';
import {
  Box,
  Paper,
  IconButton,
  Tooltip,
  Typography,
  Menu,
  MenuItem,
  Fade
} from '@mui/material';
import {
  DragIndicator,
  Delete,
  FileCopy,
  KeyboardArrowUp,
  KeyboardArrowDown
} from '@mui/icons-material';
import { acme } from '../../../theme/acmeTheme';

// Импортируем компоненты блоков
import TextBlock from './blocks/TextBlock';
import HeadingBlock from './blocks/HeadingBlock';
import ImageBlock from './blocks/ImageBlock';
import ButtonBlock from './blocks/ButtonBlock';
import DividerBlock from './blocks/DividerBlock';
import SpacerBlock from './blocks/SpacerBlock';
import ColumnsBlock from './blocks/ColumnsBlock';
import HeroBlock from './blocks/HeroBlock';
import ListBlock from './blocks/ListBlock';
import TestimonialBlock from './blocks/TestimonialBlock';
import { isAdminRole } from '../../../utils/userRoles';
import ProductBlock from './blocks/ProductBlock';
import EventBlock from './blocks/EventBlock';
import SocialIconsBlock from './blocks/SocialIconsBlock';
import FooterBlock from './blocks/FooterBlock';
import ContainerBlock from './blocks/ContainerBlock';
import UnsubscribeBlock from './blocks/UnsubscribeBlock';
import ContactUsBlock from './blocks/ContactUsBlock';

// Импортируем утилитарные функции
import { duplicateBlockWithUniqueIds } from '../utils/blockUtils';

// Утилитарная функция для throttling
const useThrottle = (callback, delay) => {
  const timeoutRef = useRef(null);
  const lastArgsRef = useRef();
  
  return useCallback((...args) => {
    lastArgsRef.current = args;
    
    if (timeoutRef.current === null) {
      timeoutRef.current = setTimeout(() => {
        callback(...lastArgsRef.current);
        timeoutRef.current = null;
      }, delay);
    }
  }, [callback, delay]);
};



const EmailCanvas = ({
  blocks,
  selectedBlock,
  onSelectBlock,
  onUpdateBlock,
  onDeleteBlock,
  onMoveBlock,
  onAddBlock,
  onReorderBlocks,
  settings,
  previewMode,
  user,
  showNotification,
}) => {
  const [draggedBlock, setDraggedBlock] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [contextBlock, setContextBlock] = useState(null);
  const [isDraggingOverEmpty, setIsDraggingOverEmpty] = useState(false);
  const canvasRef = useRef(null);
  
  // Throttling для drag events
  const lastDragOverIndex = useRef(null);

  // Мапинг типов блоков к компонентам
  const blockComponents = {
    text: TextBlock,
    heading: HeadingBlock,
    image: ImageBlock,
    button: ButtonBlock,
    divider: DividerBlock,
    spacer: SpacerBlock,
    columns: ColumnsBlock,
    'columns-3': ColumnsBlock,
    hero: HeroBlock,
    list: ListBlock,
    testimonial: TestimonialBlock,
    product: ProductBlock,
    event: EventBlock,
    'social-icons': SocialIconsBlock,
    footer: FooterBlock,
    container: ContainerBlock,
    unsubscribe: UnsubscribeBlock,
    contactUs: ContactUsBlock,
  };

  const getResponsiveStyles = () => {
    const baseStyles = {
      maxWidth: settings.width || 1200,
      margin: '0 auto',
      backgroundColor: settings.backgroundColor || '#ffffff',
      fontFamily: settings.fontFamily || 'Arial, sans-serif',
      fontSize: settings.fontSize || 14,
      lineHeight: 1.4,
    };

    switch (previewMode) {
      case 'mobile':
        return {
          ...baseStyles,
          maxWidth: '100%',
          padding: '10px',
        };
      case 'tablet':
        return {
          ...baseStyles,
          maxWidth: '100%',
          padding: '15px',
        };
      default:
        return {
          ...baseStyles,
          padding: `${settings.padding || 20}px`,
        };
    }
  };

  const handleDragStart = useCallback((e, blockId) => {
    setDraggedBlock(blockId);
    e.dataTransfer.effectAllowed = 'move';
    
    // Находим блок и сохраняем его данные для перетаскивания в колонки
    const block = blocks.find(b => b.id === blockId);
    if (block) {
      const blockData = {
        type: block.type,
        data: block.data,
        defaultData: block.data,
        fromCanvas: true,
        originalId: blockId
      };
      e.dataTransfer.setData('application/json', JSON.stringify(blockData));
    }
  }, [blocks]);

  // Упрощенный обработчик окончания drag
  const handleDragEnd = useCallback((e) => {
    console.log('🔚 Drag ended for block:', draggedBlock);
    // Очищаем состояние через минимальную задержку
    setTimeout(() => {
      setDraggedBlock(null);
      setDragOverIndex(null);
      lastDragOverIndex.current = null;
      console.log('🔚 Drag state cleared');
    }, 50);
  }, [draggedBlock]);

  // Оптимизированная функция обновления dragOverIndex
  const updateDragOverIndex = useCallback((index) => {
    if (lastDragOverIndex.current !== index) {
      setDragOverIndex(index);
      lastDragOverIndex.current = index;
    }
  }, []);

  // Throttled версия обновления (уменьшили задержку для быстрой реакции)
  const throttledUpdateDragOverIndex = useThrottle(updateDragOverIndex, 8);

  // Оптимизированный handleDragOver с throttling для drop zones
  const handleDragOver = useCallback((e, index) => {
    e.preventDefault();
    
    // Определяем тип операции
    try {
      const types = e.dataTransfer.types;
      if (types.includes('application/json')) {
        // Это либо внутренний drag, либо из библиотеки
        if (draggedBlock) {
          // Внутреннее перемещение
          e.dataTransfer.dropEffect = 'move';
          throttledUpdateDragOverIndex(index);
        } else {
          // Новый блок из библиотеки 
          e.dataTransfer.dropEffect = 'copy';
          throttledUpdateDragOverIndex(index);
        }
      }
    } catch (error) {
      // Fallback
      e.dataTransfer.dropEffect = draggedBlock ? 'move' : 'copy';
      throttledUpdateDragOverIndex(index);
    }
  }, [throttledUpdateDragOverIndex, draggedBlock]);

  // Оптимизированный handleDragLeave
  const handleDragLeave = useCallback((e) => {
    // Только очищаем индекс если действительно покидаем область
    const relatedTarget = e.relatedTarget;
    const currentTarget = e.currentTarget;
    
    // Если мышь перешла к дочернему элементу, не очищаем индекс
    if (relatedTarget && currentTarget.contains(relatedTarget)) {
      return;
    }
    
    // Проверяем границы элемента
    const rect = currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setDragOverIndex(null);
      lastDragOverIndex.current = null;
    }
  }, []);

  const handleDrop = useCallback((e, dropIndex) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('🟡 handleDrop called:', { draggedBlock, dropIndex, blocksCount: blocks.length });
    
    setDragOverIndex(null);
    lastDragOverIndex.current = null;
    
    try {
      const blockData = JSON.parse(e.dataTransfer.getData('application/json'));
      console.log('🟡 Block drop data:', { 
        type: blockData.type, 
        fromCanvas: blockData.fromCanvas, 
        fromLibrary: blockData.fromLibrary 
      });
      
      if (blockData && blockData.type) {
        // Case 1: Новый блок из библиотеки
        if (!blockData.fromCanvas && !draggedBlock) {
          console.log('🟢 Adding new block from library at position:', dropIndex);
          if (onAddBlock) {
            onAddBlock(blockData.type, blockData.defaultData || blockData.data || {}, dropIndex);
            console.log('🟢 Block added successfully');
          }
        }
        // Case 2: Внутреннее перемещение блоков в canvas
        else if (draggedBlock && blockData.fromCanvas) {
          const dragIndex = blocks.findIndex(block => block.id === draggedBlock);
          console.log('🔄 Internal drag operation:', { draggedBlock, dragIndex, dropIndex });
          
          if (dragIndex !== -1 && dragIndex !== dropIndex) {
            const newBlocks = [...blocks];
            const [draggedItem] = newBlocks.splice(dragIndex, 1);
            
            // Корректируем индекс вставки если перетаскиваем вниз
            const insertIndex = dragIndex < dropIndex ? dropIndex - 1 : dropIndex;
            newBlocks.splice(insertIndex, 0, draggedItem);
            
            console.log('🔄 Reordering:', { dragIndex, dropIndex, insertIndex });
            
            // Обновляем позиции блоков
            newBlocks.forEach((block, index) => {
              block.position = index;
            });
            
            // Обновляем порядок блоков
            if (onReorderBlocks) {
              onReorderBlocks(newBlocks);
              console.log('🔄 Blocks reordered successfully');
            }
          }
        }
        else {
          console.log('🚫 Unhandled drop case:', { 
            fromCanvas: blockData.fromCanvas, 
            draggedBlock: !!draggedBlock,
            fromLibrary: blockData.fromLibrary 
          });
        }
      }
    } catch (error) {
      console.error('Error parsing dropped data:', error);
    }
    
    setDraggedBlock(null);
  }, [draggedBlock, blocks, onReorderBlocks, onAddBlock]);

  const handleContextMenu = (e, block) => {
    e.preventDefault();
    setContextMenu({ mouseX: e.clientX, mouseY: e.clientY });
    setContextBlock(block);
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
    setContextBlock(null);
  };

  const handleDuplicateBlock = () => {
    if (!contextBlock || contextBlock.type === 'unsubscribe') {
      handleCloseContextMenu();
      return;
    }
    const newBlock = duplicateBlockWithUniqueIds(contextBlock);
    if (onAddBlock) {
      onAddBlock(newBlock.type, newBlock.data);
    }
    handleCloseContextMenu();
  };

  const handleDeleteFromContextMenu = () => {
    if (!contextBlock) {
      handleCloseContextMenu();
      return;
    }
    if (contextBlock.type === 'unsubscribe' && !isAdminRole(user?.role)) {
      showNotification?.('Unsubscribe block cannot be deleted', 'error');
      handleCloseContextMenu();
      return;
    }
    onDeleteBlock(contextBlock.id);
    handleCloseContextMenu();
  };

  const renderBlock = (block, index) => {
    const BlockComponent = blockComponents[block.type];
    
    if (!BlockComponent) {
      // Don't render unknown blocks to avoid errors during drag operations
      console.warn(`Unknown block type: ${block.type}`, block);
      return null;
    }

    const isSelected = selectedBlock?.id === block.id;
    const isProtected = block.type === 'unsubscribe' && !isAdminRole(user?.role);

    return (
      <Box
        key={block.id}
        sx={{
          position: 'relative',
          mb: block.type === 'unsubscribe' ? 0 : `${settings.blockSpacing !== undefined ? settings.blockSpacing : 5}px`,
          border: isSelected ? '2px solid #007bff' : '1px solid transparent',
          borderRadius: 1,
          cursor: 'pointer',
          '&:hover': {
            border: isSelected ? '2px solid #007bff' : '1px solid #007bff',
            '& .block-controls': {
              opacity: 1,
            },
          },
        }}
        draggable
        onDragStart={(e) => handleDragStart(e, block.id)}
        onDragEnd={handleDragEnd}
        onContextMenu={(e) => handleContextMenu(e, block)}
        onClick={(e) => {
          e.stopPropagation();
          onSelectBlock(block);
        }}
      >


        {/* Панель управления блоком */}
        <Box
          className="block-controls"
          sx={{
            position: 'absolute',
            top: -35,
            left: 0,
            right: 0,
            height: 30,
            backgroundColor: '#2c3e50',
            borderRadius: '4px 4px 0 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1,
            opacity: isSelected ? 1 : 0,
            transition: 'opacity 0.2s',
            zIndex: 1,
            pointerEvents: isSelected ? 'auto' : 'none',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <DragIndicator sx={{ color: '#ecf0f1', fontSize: 16, cursor: 'grab' }} />
            <Typography variant="caption" sx={{ color: '#ecf0f1', fontSize: 11 }}>
              {block.type.charAt(0).toUpperCase() + block.type.slice(1)}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title="Move Up">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveBlock(block.id, 'up');
                }}
                sx={{ color: '#ecf0f1', padding: 0.25 }}
              >
                <KeyboardArrowUp fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Move Down">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveBlock(block.id, 'down');
                }}
                sx={{ color: '#ecf0f1', padding: 0.25 }}
              >
                <KeyboardArrowDown fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Duplicate">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  if (block.type === 'unsubscribe') return;
                  const newBlock = duplicateBlockWithUniqueIds(block);
                  if (onAddBlock) {
                    onAddBlock(newBlock.type, newBlock.data, index + 1);
                  }
                }}
                disabled={block.type === 'unsubscribe'}
                sx={{ color: '#ecf0f1', padding: 0.25 }}
              >
                <FileCopy fontSize="small" />
              </IconButton>
            </Tooltip>

            {!isProtected && (
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteBlock(block.id);
                  }}
                  sx={{ color: '#e74c3c', padding: 0.25 }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Компонент блока */}
        <BlockComponent
          data={block.data}
          isSelected={isSelected}
          previewMode={previewMode}
          onUpdate={(newData) => onUpdateBlock(block.id, newData)}
          isReadOnly={block.type === 'unsubscribe' && !isAdminRole(user?.role)}
          blockId={block.id}
          onSelectBlock={onSelectBlock}
          onUpdateBlock={(block.type === 'columns' || block.type === 'columns-3' || block.type === 'container') ? onUpdateBlock : undefined}
          onDeleteBlock={(block.type === 'columns' || block.type === 'columns-3' || block.type === 'container') ? onDeleteBlock : undefined}
          onAddToColumn={(block.type === 'columns' || block.type === 'columns-3' || block.type === 'container') ? 
            (columnIndex, blockType, blockData, fromCanvasId = null) => {
              console.log('Adding to column/container:', { columnIndex, blockType, fromCanvasId, blockData });
              
              // Создаем новый блок для колонки/контейнера
              const newSubBlock = {
                id: Date.now().toString() + '_col_' + columnIndex,
                type: blockType,
                data: blockData || {},
                position: 0,
              };
              
              console.log('New sub block:', newSubBlock);
              
              // 🔒 БЛОКИРУЕМ race conditions - обновляем блок синхронно без задержек
              const updateResult = onUpdateBlock(block.id, (prevData) => {
                // Логируем только при отладке
                if (process.env.NODE_ENV === 'development' && Math.random() < 0.3) {
                console.log('🔄 UPDATE START - prevData:', {
                  blockType: block.type,
                  columns: prevData.columns,
                  leftContentLength: prevData.leftContent?.length || 0,
                  rightContentLength: prevData.rightContent?.length || 0,
                  contentLength: prevData.content?.length || 0,
                  timestamp: Date.now()
                });
                }
                
                const updatedData = { ...prevData };
                
                if (block.type === 'container') {
                  // Для контейнера (одна колонка)
                  updatedData.content = [...(prevData.content || []), newSubBlock];
                  console.log('✅ Added to container content:', { 
                    newLength: updatedData.content.length,
                    addedId: newSubBlock.id 
                  });
                } else if (prevData.columns === 2) {
                  // Для двухколоночного блока
                  if (columnIndex === 0) {
                    const oldLeftLength = prevData.leftContent?.length || 0;
                    updatedData.leftContent = [...(prevData.leftContent || []), newSubBlock];
                    console.log('✅ Added to leftContent:', { 
                      oldLength: oldLeftLength,
                      newLength: updatedData.leftContent.length,
                      addedId: newSubBlock.id 
                    });
                  } else {
                    // КРИТИЧНО: всегда создаем rightContent если его нет
                    console.log('📊 Before rightContent update:', {
                      hasRightContent: !!prevData.rightContent,
                      rightContentLength: prevData.rightContent?.length || 0
                    });
                    
                    if (!updatedData.rightContent) {
                      updatedData.rightContent = [];
                      console.log('🔧 Created new rightContent array');
                    }
                    updatedData.rightContent = [...updatedData.rightContent, newSubBlock];
                    
                    console.log('✅ Added to rightContent:', { 
                      length: updatedData.rightContent.length,
                      newElementId: newSubBlock.id,
                      allIds: updatedData.rightContent.map(item => item.id)
                    });
                  }
                } else {
                  // Для многоколоночного блока
                  const content = [...(prevData.content || [])];
                  if (!content[columnIndex]) content[columnIndex] = [];
                  content[columnIndex] = [...content[columnIndex], newSubBlock];
                  updatedData.content = content;
                }
                
                // Логируем только при отладке
                if (process.env.NODE_ENV === 'development' && Math.random() < 0.3) {
                console.log('🔄 UPDATE END - updatedData:', {
                  blockType: block.type,
                  columns: updatedData.columns,
                  leftContentLength: updatedData.leftContent?.length || 0,
                  rightContentLength: updatedData.rightContent?.length || 0,
                  contentLength: updatedData.content?.length || 0,
                  timestamp: Date.now()
                });
                }
                
                return updatedData;
              });
              
              console.log('📋 Update result:', updateResult);
              
              // ✅ Элемент успешно добавлен в колонку
              // Больше не удаляем оригинальный элемент автоматически
            } : undefined
          }
        />
      </Box>
    );
  };

    // Универсальный обработчик для canvas - только для пустых областей
  const handleCanvasDragOver = useCallback((e) => {
    e.preventDefault();
    
    // Определяем тип операции для правильного dropEffect
    if (draggedBlock) {
      // Это внутреннее перемещение блоков
      e.dataTransfer.dropEffect = 'move';
    } else {
      // Это новый блок из библиотеки
      e.dataTransfer.dropEffect = 'copy';
      setIsDraggingOverEmpty(true);
    }
  }, [draggedBlock]);

  // Обработчик для пустых областей canvas - только для новых блоков из библиотеки
  const handleCanvasDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setIsDraggingOverEmpty(false);
    
    // Игнорируем если это внутренний drag
    if (draggedBlock) {
      console.log('handleCanvasDrop: Ignoring internal drag operation');
      return;
    }
    
    console.log('handleCanvasDrop called (empty area):', { draggedBlock });
    
    try {
      const blockData = JSON.parse(e.dataTransfer.getData('application/json'));
      
      if (blockData && blockData.type && !blockData.fromCanvas) {
        // Добавляем новый блок в начало (позиция 0)
        console.log('Adding new block to top of canvas');
        if (onAddBlock) {
          onAddBlock(blockData.type, blockData.defaultData || blockData.data || {}, 0);
          console.log('Block added successfully');
        }
      }
    } catch (error) {
      console.error('Error parsing dropped data:', error);
    }
  }, [draggedBlock, onAddBlock]);

  // Обработчик для dragLeave на пустом канвасе
  const handleCanvasDragLeave = useCallback((e) => {
    e.preventDefault();
    // Проверяем, действительно ли мышь покинула область
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setIsDraggingOverEmpty(false);
    }
  }, []);

  // Убираем глобальный обработчик - он мешает нормальной работе

  return (
    <Box
      ref={canvasRef}
      sx={{
        minHeight: '700px',
        position: 'relative',
        pb: 4,
        pt: 4,
        // Оптимизации для плавности drag-and-drop
        willChange: 'transform',
        backfaceVisibility: 'hidden',
        transform: 'translateZ(0)', // Включаем аппаратное ускорение
      }}
      onDrop={blocks.filter(block => block.type !== 'unsubscribe').length === 0 ? handleCanvasDrop : undefined}
      onDragOver={blocks.filter(block => block.type !== 'unsubscribe').length === 0 ? handleCanvasDragOver : undefined}
    >
      {/* Email контейнер */}
      <Paper
        elevation={2}
        onDragOver={blocks.filter(block => block.type !== 'unsubscribe').length === 0 ? handleCanvasDragOver : undefined}
        onDrop={blocks.filter(block => block.type !== 'unsubscribe').length === 0 ? handleCanvasDrop : undefined}
        sx={{
          ...getResponsiveStyles(),
          minHeight: 700,
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          // Оптимизации производительности
          willChange: 'transform',
          backfaceVisibility: 'hidden',
        }}
      >
        {/* Check if canvas is empty (excluding unsubscribe blocks) */}
        {blocks.filter(block => block.type !== 'unsubscribe').length === 0 ? (
          // Пустое состояние
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 700,
              color: '#666',
              textAlign: 'center',
              p: 4,
              cursor: 'pointer',
              border: '2px dashed #ddd',
              borderRadius: 2,
              backgroundColor: '#fafafa',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: '#007bff',
                backgroundColor: '#f0f8ff',
              },
              ...(isDraggingOverEmpty && {
                borderColor: '#007bff',
                backgroundColor: '#e3f2fd',
                borderStyle: 'solid',
              })
            }}
            onDragOver={handleCanvasDragOver}
            onDragLeave={handleCanvasDragLeave}
            onDrop={handleCanvasDrop}
          >
            <Typography variant="h6" sx={{ mb: 2, color: acme.colors.mutedForegroundSolid }}>
              Your email canvas is empty
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, maxWidth: 400 }}>
              Drag any block from the library on the left to start building your email template.
              You can drop it anywhere in this area and it will be placed at the top.
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ 
              fontSize: '12px',
              fontStyle: 'italic',
              opacity: 0.7
            }}>
              💡 Tip: No need to find the exact drop zone - just drop anywhere!
            </Typography>
          </Box>
        ) : (
          // Основной контент
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {/* Обычные блоки с drop zones */}
            <Box sx={{ flex: 1 }}>
              {/* Drop zone в начале - скрытая область для drag&drop */}
              <Box
                sx={{
                  height: dragOverIndex === 0 ? '40px' : '2px',
                  padding: dragOverIndex === 0 ? '10px 0' : '0',
                  backgroundColor: 'transparent',
                  borderRadius: '4px',
                  mb: 0,
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                }}
                onDragOver={(e) => handleDragOver(e, 0)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, 0)}
              >
                {/* Визуальная синяя полоска только при драге */}
                {dragOverIndex === 0 && (
                  <>
                    <Box
                      sx={{
                        height: '4px',
                        width: '100%',
                        backgroundColor: '#007bff',
                        borderRadius: '2px',
                        border: '2px dashed #007bff',
                        transition: 'all 0.2s ease',
                      }}
                    />
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: '#007bff', 
                        fontWeight: 'bold',
                        position: 'absolute',
                        backgroundColor: 'white',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '10px',
                      }}
                    >
                      Drop here
                    </Typography>
                  </>
                )}
              </Box>
              
              {blocks
                .filter(block => block.type !== 'unsubscribe')
                                 .map((block, index) => (
                   <Fragment key={block.id}>
                     {renderBlock(block, index)}
                     
                                         {/* Drop zone после каждого блока - скрытая область для drag&drop */}
                    <Box
                      sx={{
                        height: dragOverIndex === index + 1 ? '40px' : '2px',
                        padding: dragOverIndex === index + 1 ? '10px 0' : '0',
                        backgroundColor: 'transparent',
                        borderRadius: '4px',
                        my: 0,
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                      }}
                      onDragOver={(e) => handleDragOver(e, index + 1)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, index + 1)}
                    >
                      {/* Визуальная синяя полоска только при драге */}
                      {dragOverIndex === index + 1 && (
                        <>
                          <Box
                            sx={{
                              height: '4px',
                              width: '100%',
                              backgroundColor: '#007bff',
                              borderRadius: '2px',
                              border: '2px dashed #007bff',
                              transition: 'all 0.2s ease',
                            }}
                          />
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: '#007bff', 
                              fontWeight: 'bold',
                              position: 'absolute',
                              backgroundColor: 'white',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '10px',
                            }}
                          >
                            Drop here
                          </Typography>
                        </>
                      )}
                    </Box>
                   </Fragment>
                 ))}
            </Box>
            
            {/* Unsubscribe блок внизу */}
            {blocks
              .filter(block => block.type === 'unsubscribe')
              .map((block, index) => (
                <Box key={block.id} sx={{ mt: 'auto' }}>
                  {renderBlock(block, blocks.length - 1)}
                </Box>
              ))}
          </Box>
        )}
      </Paper>

      {/* Контекстное меню */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        TransitionComponent={Fade}
      >
        <MenuItem onClick={handleDuplicateBlock} disabled={contextBlock?.type === 'unsubscribe'}>
          <FileCopy sx={{ mr: 1 }} fontSize="small" />
          Duplicate
        </MenuItem>
        <MenuItem
          onClick={handleDeleteFromContextMenu}
          disabled={contextBlock?.type === 'unsubscribe' && !isAdminRole(user?.role)}
          sx={{ color: 'error.main' }}
        >
          <Delete fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default EmailCanvas; 